// ¿Este plato quedó IMPOSIBLE de armar porque un grupo OBLIGATORIO se quedó sin
// ingredientes disponibles?
//
// POR QUÉ EXISTE: agotar ingredientes (86) no toca el plato. Si el dueño agota
// la última proteína de "Elige tu proteína" (obligatorio), el plato sigue en el
// menú, el cliente lo abre… y no puede terminar: el modal le exige elegir algo
// que ya no existe, y si la pantalla oculta el grupo entero para desatascar el
// botón, el pedido llega al servidor sin ese grupo y `checkModifierSelections`
// lo rechaza con 422 — en el kiosko, DESPUÉS de cobrar. Las dos salidas eran un
// callejón; la honesta es no dejar entrar: el plato se muestra como no
// disponible, igual que si el dueño lo hubiera agotado a mano.
//
// LA REGLA ES `required`, NO `min_selections`. Es la misma que aplica el
// servidor en lib/menu/validateModifierSelections: con `is_required=false` el
// mínimo es 0 aunque `min_selections` diga otra cosa, así que un grupo opcional
// entero agotado NO vuelve invendible el plato — el cliente simplemente lo
// salta. Divergir de esa semántica es lo que tumbó a Raiz 58 (2026-05-28).
//
// Un grupo obligatorio SIN ninguna opción (dato roto: se creó el grupo y nunca
// se le agregaron opciones, o se borraron todas) cae en el mismo saco: exige un
// mínimo de 1 sobre un conjunto vacío. Hoy las pantallas lo ignoraban y el 422
// aparecía al cobrar.

export type SoldOutCheckGroup = {
  /** `modifier_groups.is_required` (el kiosko lo aliasa como `required`). */
  required: boolean | null | undefined
  options: ReadonlyArray<{ is_available?: boolean | null }> | null | undefined
}

/** Una opción cuenta como vendible salvo que esté explícitamente agotada —
 *  mismo criterio (`!== false`) que usan los modales al pintarla. */
function hasSellableOption(options: SoldOutCheckGroup['options']): boolean {
  return (options ?? []).some((o) => o.is_available !== false)
}

/** Grupo OBLIGATORIO sin una sola opción vendible. */
export function isGroupUnsellable(group: SoldOutCheckGroup): boolean {
  if (group.required !== true) return false
  return !hasSellableOption(group.options)
}

/** ¿Alguno de los grupos del plato lo vuelve imposible de armar? */
export function hasUnsellableRequiredGroup(
  groups: ReadonlyArray<SoldOutCheckGroup>,
): boolean {
  return groups.some(isGroupUnsellable)
}

/**
 * La misma pregunta, sobre el ítem tal como lo traen las consultas de menú:
 * `item_modifier_groups[].modifier_groups`.
 *
 * Acepta las DOS formas del campo a propósito: el kiosko y el microsite lo
 * aliasan en el select (`required:is_required`) y el POS se queda con el nombre
 * de la columna (`is_required`). Un adaptador por pantalla habría sido tres
 * mapeos que envejecen por separado.
 */
export type NestedModifierItem = {
  item_modifier_groups?: ReadonlyArray<{
    modifier_groups?: {
      required?: boolean | null
      is_required?: boolean | null
      modifier_options?: ReadonlyArray<{ is_available?: boolean | null }> | null
    } | null
  }> | null
  /** Forma del POS: los grupos ya vienen aplanados con sus `options`. */
  modifier_groups?: ReadonlyArray<{
    required?: boolean | null
    is_required?: boolean | null
    options?: ReadonlyArray<{ is_available?: boolean | null }> | null
  }> | null
}

export function itemHasUnsellableRequiredGroup(item: NestedModifierItem): boolean {
  const nested = (item.item_modifier_groups ?? [])
    .map((link) => link.modifier_groups)
    .filter((g): g is NonNullable<typeof g> => !!g)
    .map((g) => ({ required: g.required ?? g.is_required, options: g.modifier_options }))
  const flat = (item.modifier_groups ?? []).map((g) => ({
    required: g.required ?? g.is_required,
    options: g.options,
  }))
  return hasUnsellableRequiredGroup([...nested, ...flat])
}
